import { combineLatest } from 'rxjs';
import React, { memo, useEffect, useState, useRef } from 'react';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';

import adherenceService from 'services/adherence';
import categoryService from 'services/category';
import selectedVentureService from 'services/profile';
import Toolbar from 'components/Layout/Toolbar';
import DialogCloseToExpiration from '../Component/DialogCloseToExpiration';
import AdherenceGraph from '../Graphs/AdherenceGraph';
import { ICategory } from 'interfaces/category';
import { IAdherence } from 'interfaces/dashboard';
import SnapshotPicker from '../Component/SnapshotPicker';
import { useSnapshots } from '../context/SnapshotsContext';

const graphColors = ['#961132', '#808080', '#8b7c3f'];

const Adherence: React.FC = memo(() => {
  const containerToPrint = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<ICategory[]>();
  const [data, setData] = useState<IAdherence[][]>();
  const [dialogProps, setDialogProps] = useState<{
    open: boolean;
    currentCategory?: ICategory;
  }>({
    open: false,
  });
  const [loading, setLoading] = useState(false);
  const { getUrlWithSnap, selectedSnap, selectedSnapId } = useSnapshots();

  useEffect(() => {
    setLoading(true);
    selectedVentureService.getSelectedVenture().subscribe((venture) => {
      if (venture) {
        categoryService.fecthCategory(venture.id, { refresh: true }).subscribe((categories) => {
          const categoriesSorted = categories.data
            ? categories.data.sort((a, b) => a.id - b.id)
            : [];

          setCategories(categoriesSorted);
          combineLatest(
            categoriesSorted.map((category) => adherenceService.fetch(category.id, selectedSnapId))
          ).subscribe((data) => {
            setData(data);
            setLoading(false);
          });
        });
      }
    });
  }, [selectedSnapId]);

  const handleOpenDialogCloseToExpiration = (currentCategory: ICategory) => () => {
    setDialogProps({
      open: true,
      currentCategory,
    });
  };

  const handleCloseDialogCloseToExpiration = () => {
    setDialogProps((i) => ({
      ...i,
      open: false,
    }));
  };

  console.log('Adherence data:', data);

  return (
    <div style={{ paddingBottom: 8 }}>
      <DialogCloseToExpiration
        {...dialogProps}
        snapshot={selectedSnap}
        onClose={handleCloseDialogCloseToExpiration}
      />
      <Toolbar
        title="Dashboard"
        hideVentureSelect
        containerToPrint={containerToPrint}
        breadcrumbs={[
          {
            title: 'Dashboard',
            href: getUrlWithSnap('/documentation/adherence'),
          },
        ]}
      />
      {loading && <LinearProgress color="secondary" />}

      <div
        ref={containerToPrint}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '48px',
          backgroundColor: 'white',
          paddingBottom: 12,
        }}
      >
        <div style={{ width: 'fit-content', alignSelf: 'flex-end' }}>
          <SnapshotPicker />
        </div>

        {data && (
          <Grid
            container
            justify="center"
            alignItems="center"
            spacing={1}
            style={{ marginTop: '20px' }}
          >
            {data.map((_data, idx) => (
              <Grid key={`AdherenceGraph-${idx}`} item xs={12} sm={12} md={6} xl={4}>
                <AdherenceGraph
                  data={_data}
                  title={categories[idx].name}
                  fillColor={
                    graphColors[idx] ||
                    `#${Math.floor(Math.random() * 16777215)
                      .toString(16)
                      .padStart(6, '0')}`
                  }
                  onOpenDialogCloseToExpiration={handleOpenDialogCloseToExpiration(categories[idx])}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </div>
  );
});

export default Adherence;
